import { Component, Input, OnInit } from '@angular/core';
import GuestMailingStatusIconTemplate from './guest-mailing-status-icon.component.html';
import { Mailing } from '../../../../shared/models/mailings/mailing.model';

@Component({
  selector: 'guest-mailing-status-icon',
  template: GuestMailingStatusIconTemplate
})
export class GuestMailingStatusIconComponent implements OnInit {
  @Input() mailing: string;
  @Input() mailingStatuses: any[] = [];
  @Input() mailings: Mailing[] = [];
  state: any;
  icon: string = null;
  message: string = null;
  statusColor: string = '#c2c2c2';
  private status: 'sent_success' | 'sent_error' | 'unsent' = 'unsent';

  constructor() { }

  ngOnInit() {
    const mailingId = parseInt(this.mailing.split('_')[1], 10);
    const mailingItems = (this.mailingStatuses || []).filter(m => m.mailing_id === mailingId);
    const latest = this.pickLatestMailingStatus(mailingItems);

    if (latest) {
      this.state = latest.state;
      this.status = this.resolveStatus(latest.state, latest.send_error);
      this.message = this.formatMessage(latest.send_error);
    } else {
      this.state = null;
      this.status = 'unsent';
      this.message = null;
    }

    this.statusColor = this.getStatusColor(this.status);
    this.icon = this.resolveIcon(mailingId);
  }

  private pickLatestMailingStatus(entries: any[]): any | null {
    if (!entries || entries.length === 0) {
      return null;
    }

    return entries.reduce((latest: any, current: any) => {
      if (!latest) {
        return current;
      }

      const latestTime = latest.real_send_date ? new Date(latest.real_send_date).getTime() : 0;
      const currentTime = current.real_send_date ? new Date(current.real_send_date).getTime() : 0;

      if (currentTime === latestTime) {
        return (current.id ?? 0) > (latest.id ?? 0) ? current : latest;
      }

      return currentTime > latestTime ? current : latest;
    }, null);
  }

  private resolveIcon(mailingId: number): string | null {
    const match = (this.mailings || []).find(m => m.id === mailingId);
    return match?.icon ?? null;
  }

  private resolveStatus(state: string | null, sendError: string | null): 'sent_success' | 'sent_error' | 'unsent' {
    const normalizedState = this.normalizeState(state);
    if (this.isSuccessState(normalizedState)) {
      return 'sent_success';
    }

    if (this.isErrorState(normalizedState) || this.hasMeaningfulError(sendError)) {
      return 'sent_error';
    }

    return 'unsent';
  }

  private normalizeState(state: string | null | undefined): string | null {
    const normalized = state?.toString().trim().toLowerCase();
    if (!normalized || normalized === 'null' || normalized === 'undefined') {
      return null;
    }
    return normalized;
  }

  private hasMeaningfulError(error: string | null | undefined): boolean {
    if (error == null) {
      return false;
    }

    const normalized = error.toString().trim();
    if (!normalized) {
      return false;
    }

    const lowered = normalized.toLowerCase();
    return lowered !== 'null' && lowered !== 'undefined' && lowered !== 'ok' && lowered !== 'success';
  }

  private isSuccessState(state: string | null): boolean {
    if (!state) {
      return false;
    }
    return ['sent', 'success', 'sent_success', 'completed', 'delivered', 'ok', 'done'].includes(state);
  }

  private isErrorState(state: string | null): boolean {
    if (!state) {
      return false;
    }
    return ['error', 'failed', 'failure', 'send_error', 'sent_error', 'bounce', 'rejected', 'blocked', 'delivery_error'].includes(state);
  }

  private getStatusColor(status: 'sent_success' | 'sent_error' | 'unsent'): string {
    switch (status) {
      case 'sent_success':
        return '#1ab394';
      case 'sent_error':
        return '#ed5565';
      default:
        return '#c2c2c2';
    }
  }

  private formatMessage(raw: unknown): string | null {
    if (raw == null) {
      return null;
    }

    const value = raw.toString().trim();
    if (!value || value.toLowerCase() === 'null') {
      return null;
    }

    return value.replace(/\n/g, '<br/>');
  }

}
